import { type ValueEvent } from '@adornis/base/utilTypes.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { LoaderController } from '@adornis/chemistry/controllers/loader-controller.js';
import { WindowSizeController } from '@adornis/chemistry/controllers/window-size-controller.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { permissionGuard } from '@adornis/user-forms/permission-guard.js';
import { ANY_CONTEXT } from '@adornis/users/db/a-roles.js';
import { CampusArea } from '@chancenwerk/campus-learning-content/entities/CampusSection.js';
import { availableCampusSubjects } from '@chancenwerk/db/enums/CampusSubject.js';
import { ChancenPermission } from '@chancenwerk/db/permissions.js';
import '@chancenwerk/design/components/cr-button.js';
import '@chancenwerk/design/components/cr-image.js';
import '@chancenwerk/design/components/fonts.js';
import { html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { guard } from 'lit/directives/guard.js';
import g_logoBiologie from '../assets/Biologie_Alpha.png';
import g_logoChemie from '../assets/Chemie_Alpha.png';
import g_logoPolitik from '../assets/Politik.png';
import g_logoLandingOne from '../assets/landing_1_alpha.png';
import g_logoLandingTwo from '../assets/landing_2_alpha.png';
import g_logoLandingThree from '../assets/landing_3_alpha.png';
import g_logoChancenkurve from '../assets/logo_chancenkurve.png';
import '../components/campus-component-section-lesson-list.js';

/**
 * @customElement campus-page-lessons
 */
@customElement('campus-page-lessons')
export class CampusPageLessons extends ChemistryLitElement {
  // Filtering properties
  @state() private readonly _userCheckedSubjects = new RXController<string[]>(this, []);
  @state() private readonly _userSearchText = new RXController<string>(this, '');

  @state() private _updateList = 0;

  @state() size = new WindowSizeController(this);

  l = new LoaderController(this);

  override render() {
    return permissionGuard(ChancenPermission.campus.READ_CONTENT, () => this.l.guard(this._content()), {
      forbiddenTemplate: html`<campus-page-no-permission></campus-page-no-permission>`,
      context: ANY_CONTEXT,
    });
  }

  private _content() {
    return html`
      <cr-flex padding="${this.size.mobile ? 'md' : 'lg'}" space="lg" ${css({ minHeight: '100vh' })}>
        <campus-component-eventlist ?editingMode=${false} .area=${CampusArea.STUDENTS}></campus-component-eventlist>
        <cr-flex space="md">
          <cr-h2> Alle Lektionen </cr-h2>
          <cr-text> Hier werden alle verfügbaren Lektionen für deine Jahrgangsstufe aufgelistet. </cr-text>
          <cr-flex horizontal space-between>
            ${[
              /* g_logoChancenkurve, */
              g_logoLandingThree,
              g_logoLandingOne,
              g_logoLandingTwo,
              g_logoChemie,
              g_logoBiologie,
              g_logoPolitik,
            ].map(
              (img, i) => html`
                <cr-flex
                  crossaxis-center
                  space="md"
                  ${css({ cursor: 'pointer' })}
                  @click=${() => {
                    const index = this._userCheckedSubjects.value.findIndex(
                      subject => subject === availableCampusSubjects[i + 1],
                    );
                    if (index === -1) {
                      this._userCheckedSubjects.next([availableCampusSubjects[i + 1] ?? 'Kein Mapping']);
                    } else {
                      this._userCheckedSubjects.next([]);
                    }

                    this._updateList = this._updateList + 1;
                  }}
                >
                  <cr-image
                    disabled
                    loading="eager"
                    ${css({
                      width: '10vw',
                      height: '10vw',
                      opacity:
                        this._userCheckedSubjects.value.length &&
                        !this._userCheckedSubjects.value.includes(availableCampusSubjects[i + 1])
                          ? 0.4
                          : 1,
                    })}
                    is-url
                    .value="${img}"
                    mode="contain"
                  ></cr-image>
                  <cr-text> ${availableCampusSubjects[i + 1]} </cr-text>
                </cr-flex>
              `,
            )}
          </cr-flex>
        </cr-flex>
        <cr-flex
          ${css({ position: 'sticky', top: '0', zIndex: '1', background: this.colors.neutral.shade(50) })}
          padding="sm"
          w100
        >
          <cr-card horizontal crossaxis-center space="sm">
            <cr-input
              flex
              @value-changed=${(e: ValueEvent<string>) => {
                if (this._userSearchText.value === e.detail.value) return;
                this._userSearchText.next(e.detail.value);
              }}
              placeholder="Suchen..."
              icon="search"
            ></cr-input>
          </cr-card>
        </cr-flex>

        <cr-flex space="sm">
          ${guard(
            [this._userCheckedSubjects.value, this._userSearchText.value, this._updateList],
            () => html`
              <campus-component-section-lesson-list
                ${css({ margin: this.spacing.md })}
                .showSearchBar=${false}
                .mode=${'play'}
                .userCheckedSubjects=${this._userCheckedSubjects.value}
                .userHideSubjects=${[availableCampusSubjects[0]]}
                .userSearchText=${this._userSearchText.value}
              ></campus-component-section-lesson-list>
            `,
          )}
        </cr-flex>
      </cr-flex>
    `;
  }
}
